import React, { useEffect, useState } from "react";
import { Plus, Pencil, KeyRound, Ban, Check, ShieldCheck, User as UserIcon, Loader2, Trash2, Eye } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/contexts";
import { fmtDate } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { toSmartTitleCase } from "../lib/formatters";

function UserForm({ open, onClose, initial, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState({ name: "", email: "", role: "staff", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) setF({ name: initial.name || "", email: initial.email || "", role: initial.role || "staff", password: "" });
    else setF({ name: "", email: "", role: "staff", password: "" });
  }, [initial, open]);

  const submit = async (e) => {
    e.preventDefault();
    // Client-side validation
    const missing = [];
    if (!f.name?.trim()) missing.push("Full Name");
    if (!f.email?.trim()) missing.push("Email");
    if (missing.length) { toast.error(`Please fill in: ${missing.join(", ")}`); return; }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRe.test(f.email.trim())) {
      toast.error(`Invalid email format: "${f.email.trim()}" — please enter a valid email like name@example.com`);
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/users/${initial.id}`, { name: f.name, email: f.email, role: f.role });
        toast.success("User updated");
      } else {
        if (!f.password || f.password.length < 6) { toast.error("Password must be at least 6 characters"); setSaving(false); return; }
        await api.post("/users", f);
        toast.success("User created");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit User" : "Add New User"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full Name</Label>
            <Input required data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} onBlur={(e) => setF((s) => ({ ...s, name: toSmartTitleCase(e.target.value) }))} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" required data-testid="user-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
          <div>
            <Label>Role</Label>
            <select data-testid="user-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              <option value="staff">Sales Staff</option>
              <option value="unit_head">Unit Head (view only)</option>
              <option value="manager">Manager</option>
            </select>
          </div>
          {!isEdit && (
            <div>
              <Label>Initial Password</Label>
              <Input type="text" required minLength={6} data-testid="user-password" placeholder="Minimum 6 characters"
                value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">Share this password with the user securely. They can change it after login (via reset).</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="user-save" disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save Changes" : "Create User")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onClose, user }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setPassword(""); }, [open]);

  const generate = () => {
    // simple random password of 10 chars with a symbol
    const s = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let p = "";
    for (let i = 0; i < 10; i++) p += s[Math.floor(Math.random() * s.length)];
    setPassword(p + "!");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { password });
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Reset Password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">Set a new password for <span className="font-semibold text-neutral-900">{user?.name}</span> ({user?.email}).</p>
          <div>
            <Label>New Password</Label>
            <div className="flex gap-2 mt-1">
              <Input type="text" required minLength={6} data-testid="reset-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 characters" />
              <Button type="button" variant="outline" data-testid="generate-password" onClick={generate}>Generate</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Copy and share this password with the user securely. It replaces their current password immediately.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="reset-password-submit" disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ManageUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/users"); setUsers(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (u) => {
    const nextActive = !(u.active !== false);
    const verb = nextActive ? "reactivate" : "deactivate";
    if (!confirm(`${verb === "deactivate" ? "Deactivate" : "Reactivate"} ${u.name}? ${verb === "deactivate" ? "They will lose access immediately. Their records stay intact." : ""}`)) return;
    try {
      await api.patch(`/users/${u.id}/status`, { active: nextActive });
      toast.success(`${u.name} ${verb}d`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/users/${deleting.id}`);
      toast.success(`${deleting.name} permanently deleted`);
      setDeleting(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
    finally { setDeleteBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">User Management</h1>
          <p className="text-sm text-muted-foreground">Add, edit, deactivate team members and reset their passwords.</p>
        </div>
        <Button data-testid="new-user-btn" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-primary hover:bg-primary/90 text-white">
          <Plus className="w-4 h-4 mr-2" />Add User
        </Button>
      </div>

      <div className="bg-white rounded-md border border-border overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-primary" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Joined</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="users-list">
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                const isActive = u.active !== false;
                return (
                  <tr key={u.id} className={`border-t border-border ${!isActive ? "bg-neutral-50/70" : ""}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${u.role === "manager" ? "bg-primary/10 text-primary" : "bg-neutral-100 text-neutral-700"}`}>
                          {u.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </div>
                        <div>
                          <div className={`font-semibold ${!isActive ? "text-muted-foreground line-through" : ""}`}>{u.name}</div>
                          {isSelf && <div className="text-[10px] uppercase tracking-widest text-primary">You</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-neutral-700">{u.email}</td>
                    <td className="px-5 py-3">
                      {u.role === "manager"
                        ? <Badge className="bg-primary text-white hover:bg-primary/90"><ShieldCheck className="w-3 h-3 mr-1" />Manager</Badge>
                        : u.role === "unit_head"
                        ? <Badge className="bg-sky-100 text-sky-900 border border-sky-300 hover:bg-sky-100" data-testid={`role-unit-head-${u.id}`}><Eye className="w-3 h-3 mr-1" />Unit Head</Badge>
                        : <Badge variant="secondary"><UserIcon className="w-3 h-3 mr-1" />Sales Staff</Badge>}
                    </td>
                    <td className="px-5 py-3">
                      {isActive
                        ? <span className="inline-flex items-center gap-1.5 text-xs text-green-800 bg-green-100 px-2 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-green-600" />Active</span>
                        : <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600 bg-neutral-200 px-2 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-neutral-500" />Deactivated</span>}
                    </td>
                    <td className="px-5 py-3 text-xs text-muted-foreground">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button data-testid={`edit-user-${u.id}`} size="sm" variant="ghost"
                          onClick={() => { setEditing(u); setFormOpen(true); }} title="Edit">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`reset-user-${u.id}`} size="sm" variant="ghost"
                          onClick={() => setResetting(u)} title="Reset password">
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        {!isSelf && (
                          <Button data-testid={`toggle-user-${u.id}`} size="sm" variant="ghost"
                            onClick={() => toggleStatus(u)}
                            title={isActive ? "Deactivate" : "Reactivate"}
                            className={isActive ? "text-orange-700 hover:text-orange-800 hover:bg-orange-50" : "text-green-700 hover:text-green-800 hover:bg-green-50"}>
                            {isActive ? <Ban className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                        {!isSelf && (
                          <Button data-testid={`delete-user-${u.id}`} size="sm" variant="ghost"
                            onClick={() => setDeleting(u)}
                            title="Permanently delete"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-secondary/50 border border-border rounded-md p-4 text-xs text-muted-foreground">
        <div className="font-semibold text-neutral-800 mb-1">Deactivate vs Delete</div>
        <b>Deactivate</b> (ban icon) revokes sign-in access immediately, keeps the user record and can be reversed any time. <b>Delete</b> (trash icon) permanently removes the user; contacts, deals and notes they created stay intact and still display their name as the author, but the account cannot be restored.
      </div>

      <UserForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} onSaved={load} />
      <ResetPasswordDialog open={!!resetting} onClose={() => setResetting(null)} user={resetting} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Permanently delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <span className="font-semibold text-neutral-900">{deleting?.name}</span> ({deleting?.email}).
              <br /><br />
              This <b>cannot be undone</b>. The account and its sign-in access are removed immediately. Contacts, deals and notes they created stay intact and continue to show their name as the author. If you might want to restore access later, use <b>Deactivate</b> instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={confirmDelete} disabled={deleteBusy}
              className="bg-destructive text-white hover:bg-destructive/90">
              {deleteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
